import { VITE_FORGE_URL } from '@constants';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import MarkdownText from 'components/Widget/MarkdownText';
import Thumbnail, { ThumbnailSize } from 'components/Widget/Thumbnail';
import { getBoundingBox } from 'dragonmouse-shared/geometry';
import {
	ChatEntryRestriction,
	ChatStatus,
	FRAME_GAP,
	SceneChat,
	SystemChatEntry,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { useEventListener } from 'events/EventManager';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import { MenuItemAction } from 'menu/MenuItem';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import store from 'redux/store';
import {
	selectActiveScene,
	selectActiveStyle,
	selectFrames,
} from 'redux/db/reducers';
import {
	canEdit,
	ChatMessageActionPart,
	ChatMessageFramesPart,
	ChatMessageTextPart,
	getFrameDisplayBounds,
	getFrames,
	parseFramesXml,
	splitMessageWithFrames,
} from 'redux/db/util';
import {
	centerCanvasAroundSceneBoundingBox,
	setCenterFramesForChatId,
	showContextMenu,
} from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { displayPromptModal, displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer, selectActiveSceneChatOrDraft } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { onFileDropped } from 'utils';
import ChatDebugOverlay from '../../ChatDebugOverlay';
import SceneChatInput from '../../ChatInput/SceneChatInput';
import QueueIndicator from '../../QueueIndicator';
import { useChatScroll } from '../../hooks/useChatScroll';
import useCopyMessage from '../../hooks/useCopyMessage';
import FeedbackBar from '../FeedbackBar';
import UserMessage from '../UserMessage';
import SceneActionCard from './SceneActionCard';
import * as Base from '../styles';
import * as S from './styles';

const SceneConversation = () => {
	const containerRef = useRef<HTMLDivElement>(null);

	// spacer div we will use to force user's last input to scroll to the top of their screen when they submit a prompt
	const spacerRef = useRef<HTMLDivElement>(null);

	const dispatch = useDispatch();
	const theme = useTheme();
	const [isDraggingFile, setIsDraggingFile] = useState(false);
	const { onCopyMessage } = useCopyMessage(splitMessageWithFrames);

	const { activeChat, lastFramesBlock, scene, isMobile, frames } = useSelector(
		(r: RootReducer) => {
			const chat = selectActiveSceneChatOrDraft(r);
			const lastEntry = chat?.entries?.length
				? chat.entries[chat.entries.length - 1]
				: null;
			const lastMsg = lastEntry && (lastEntry.kind === 'user' || lastEntry.kind === 'system')
				? lastEntry.message
				: null;
			const matches = lastMsg?.match(/<frames>[\s\S]*?<\/frames>/g);
			return {
				activeChat: chat,
				// The last <frames> block in the latest entry's message. String value-equality
				// through shallowEqual means streamed text growth between/around frame blocks
				// doesn't churn this — it only changes when a new block actually arrives.
				lastFramesBlock: matches ? matches[matches.length - 1] : null,
				activeStyle: selectActiveStyle(r.db, r.mode.activeSceneId),
				scene: selectActiveScene(r.db, r.mode.activeSceneId),
				isMobile: selectIsMobile(r.mode),
				frames: selectFrames(r.db, true),
			};
		},
		shallowEqual
	);

	const frameIdSet = useMemo(() => new Set(frames.map((f) => f.id)), [frames]);

	const lastSceneEntry =
		activeChat?.entries && activeChat.entries.length > 0
			? activeChat.entries[activeChat.entries.length - 1]
			: null;
	const lastChatMessage =
		lastSceneEntry && (lastSceneEntry.kind === 'user' || lastSceneEntry.kind === 'system')
			? lastSceneEntry.message
			: null;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// Use shared scroll hook
	const { isScrolledToBottom, isRestoring, onJumpToBottom } = useChatScroll({
		containerRef,
		spacerRef,
		entries: activeChat?.entries || [],
		activeChatId: activeChat?.id || null,
		lastChatMessage,
		shouldSkipAutoScroll: () => false,
		isInitialized: !!scene?.metadata,
	});

	// When MenuItem emits scroll-chat-to-frame, scroll to the thumbnail with that frameId
	useEventListener(
		'scroll-chat-to-frame',
		useCallback((frameId: string) => {
			const tryScroll = (retries: number) => {
				if (!containerRef.current) return;
				const frameElement = containerRef.current.querySelector(`[data-frame-id="${frameId}"]`);
				if (frameElement) {
					const frameOffset = (frameElement as HTMLElement).offsetTop;
					const containerHeight = containerRef.current.clientHeight;
					containerRef.current.style.scrollBehavior = 'smooth';
					containerRef.current.scrollTop = frameOffset - containerHeight / 2;
				} else if (retries > 0) {
					requestAnimationFrame(() => tryScroll(retries - 1));
				}
			};
			tryScroll(10);
		}, [])
	);

	// When comment navigation emits scroll-chat-to-position, restore scroll position
	useEventListener(
		'scroll-chat-to-position',
		useCallback((position: { scrollTop: number; scrollHeight: number }) => {
			if (!containerRef.current) return;
			const currentHeight = containerRef.current.scrollHeight;

			let targetTop: number;
			if (!position.scrollHeight || Math.abs(currentHeight - position.scrollHeight) < 2) {
				targetTop = position.scrollTop;
			} else {
				targetTop = (position.scrollTop / position.scrollHeight) * currentHeight;
			}

			if (!Number.isFinite(targetTop)) return;
			containerRef.current.style.scrollBehavior = 'smooth';
			containerRef.current.scrollTop = targetTop;
		}, [])
	);

	// Recenter the canvas on a <frames> block. Safe to call repeatedly with the same block.
	const recenterOnFramesBlock = useCallback(
		(framesBlock: string) => {
			const frameIds = parseFramesXml(framesBlock);
			const dbFrames = getFrames(frameIds);
			if (dbFrames.length === 0) return;

			// Use real bounds for any frame already loaded into the db; for any not-yet-loaded
			// frame, synthesize its position from a loaded anchor. The anchor's row position
			// comes from its index in frameIds (the chat's declared order), NOT its index in
			// dbFrames — getFrames() returns db-order, which doesn't match row-order.
			// Synthesis assumes the row is uniform-sized frames separated by FRAME_GAP, which
			// matches how the generator currently lays out a row.
			const loadedById = new Map(dbFrames.map((f) => [f.id, getFrameDisplayBounds(f)]));
			const anchorId = dbFrames[0].id;
			const anchorBounds = loadedById.get(anchorId)!;
			const anchorIndex = frameIds.indexOf(anchorId);
			const bounds = frameIds.map((id, index) => {
				const loaded = loadedById.get(id);
				if (loaded) return loaded;
				return {
					x: anchorBounds.x + (index - anchorIndex) * (FRAME_GAP + anchorBounds.width),
					y: anchorBounds.y,
					width: anchorBounds.width,
					height: anchorBounds.height,
				};
			});
			dispatch(
				centerCanvasAroundSceneBoundingBox({
					boundingBox: getBoundingBox(bounds),
					animated: true,
				})
			);
		},
		[dispatch]
	);

	// Follows the frames a chat generates, for the chat the user submitted in. The
	// intent names its chat, so showing a different one is not something to follow —
	// and moving off the followed chat ends the follow outright, the same way panning
	// or opening a frame does, rather than resuming if the user comes back.
	// Within the followed chat this fires only on a genuinely new <frames> block:
	// shallowEqual + string value-equality dedupe identical blocks across renders.
	useEffect(() => {
		const followedChatId = store.getState().mode.centerFramesForChatId;
		if (!followedChatId) return;
		if (followedChatId !== activeChat?.id) {
			dispatch(setCenterFramesForChatId(null));
			return;
		}
		if (!lastFramesBlock) return;
		recenterOnFramesBlock(lastFramesBlock);
	}, [lastFramesBlock, activeChat?.id, recenterOnFramesBlock, dispatch]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onContextMenuInMessage = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(false);
	};

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'scene',
		chatId: activeChat?.id || null,
	});

	const onFeedback = (chat: SceneChat, entryIndex: number, feedback: 'helpful' | 'not_helpful') => {
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		const entry = chat.entries[entryIndex] as SystemChatEntry;
		const newFeedback = entry.feedback === feedback ? null : feedback;

		// For archived parent chats, apply feedback to our local parentChat state
		// (the store doesn't track it). For the active chat, forge.sceneChatFeedback handles it.
		const isParent = parentChat?.id === chat.id;
		const applyLocal = (comment: string | null) => {
			if (!isParent) return;
			setParentChat((prev) => {
				if (!prev) return prev;
				const entries = [...prev.entries];
				entries[entryIndex] = {
					...(entries[entryIndex] as SystemChatEntry),
					feedback: newFeedback,
					feedbackComment: comment,
				};
				return { ...prev, entries };
			});
		};

		applyLocal(null);
		forge.sceneChatFeedback({ chatId: chat.id, entryIndex, feedback: newFeedback });

		if (newFeedback === 'not_helpful') {
			displayPromptModal({
				title: 'What went wrong?',
				description: 'Tell us what could be improved.',
				cancelLabel: 'Dismiss',
				submitLabel: 'Submit',
				isMultilineInput: true,
				onSubmit: (value?: string) => {
					const comment = value?.trim();
					if (comment) {
						applyLocal(comment);
						forge.sceneChatFeedback({
							chatId: chat.id,
							entryIndex,
							feedback: 'not_helpful',
							comment,
						});
					}
				},
			});
		}
	};

	const onDrop = (e: React.DragEvent) => {
		setIsDraggingFile(false);

		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		if (!activeChat) return;

		// Use the utility handler
		onFileDropped(fileCallbacks)(e);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	// Parent chat (for chats that were upgraded from an older version). Fetched once, no long poll.
	const [parentChat, setParentChat] = useState<SceneChat | null>(null);
	const parentChatId = activeChat?.metadata?.parentChatId || null;

	useEffect(() => {
		if (!parentChatId) {
			setParentChat(null);
			return;
		}
		let cancelled = false;
		// One-shot fetch (no `since` param → server returns immediately without long polling)
		fetch(`${VITE_FORGE_URL}/poll/scene-chat/${parentChatId}`, { credentials: 'include' })
			.then((r) => r.json())
			.then((data) => {
				if (cancelled) return;
				setParentChat(data?.sceneChat || null);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error('Failed to fetch parent chat', err);
			});
		return () => {
			cancelled = true;
		};
	}, [parentChatId]);

	const renderEntries = (chat: SceneChat, options: { isArchived?: boolean } = {}) => {
		const { isArchived = false } = options;
		const entries = chat.entries;
		const keyPrefix = isArchived ? 'parent-' : '';
		const isLastEntry = (index: number) => index === entries.length - 1;
		// Archived chats are never generating, so only check processing state for the live chat
		const isChatProcessing =
			!isArchived &&
			(activeChat?.metadata?.status === ChatStatus.PROCESSING ||
				activeChat?.metadata?.status === ChatStatus.STOPPABLE);

		return entries.map((entry, index) => {
			if (
				entry.kind === 'system' &&
				(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
			) {
				const systemEntry = entry as SystemChatEntry;

				// Summary entries are kept in the data (LLM context) but hidden in the UI
				if (systemEntry.isSummary) {
					return null;
				}

				const parts = splitMessageWithFrames(entry.message);
				return (
					<Base.SystemMessage
						key={keyPrefix + index}
						className="system-message"
						isMobile={isMobile}
						onContextMenu={onContextMenuInMessage}
					>
						{parts.map((part, i) => {
							if (part.type === 'text') {
								const textPart = part as ChatMessageTextPart;
								return (
									<MarkdownText
										key={i}
										frameIds={frameIdSet}
										onContextMenu={onContextMenuInMessage}
									>
										{textPart.content}
									</MarkdownText>
								);
							} else if (part.type === 'frames') {
								const framesPart = part as ChatMessageFramesPart;
								return (
									<S.Frames
										key={i}
										onWheel={(e) => {
											e.stopPropagation();
											e.currentTarget.scrollLeft += e.deltaX;
										}}
									>
										{framesPart.frameIds.map((frameId) => (
											<Thumbnail
												key={frameId}
												frameId={frameId}
												size={ThumbnailSize.CHAT}
												selectOnClick
												showPreviewAction
											/>
										))}
									</S.Frames>
								);
							} else if (part.type === 'action') {
								const actionPart = part as ChatMessageActionPart;
								return (
									<SceneActionCard
										key={i}
										action={actionPart.action}
										isLastEntry={!isArchived && isLastEntry(index)}
									/>
								);
							}
							return null;
						})}
						<FeedbackBar
							feedback={systemEntry.feedback}
							isLastEntry={!isArchived && isLastEntry(index)}
							isGenerating={!isArchived && isLastEntry(index) && isChatProcessing}
							onCopy={() => onCopyMessage(index, entry.message)}
							onFeedback={(fb) => onFeedback(chat, index, fb)}
						/>
					</Base.SystemMessage>
				);
			}
			if (entry.kind === 'user') {
				const userEntry = entry as UserChatEntry;
				return (
					<UserMessage
						key={keyPrefix + index + 'user'}
						message={entry.message}
						isMobile={isMobile}
						attachments={userEntry.attachments}
						sourceFrameIds={userEntry.sourceFrameIds}
						onContextMenu={onContextMenuInMessage}
					/>
				);
			}
		});
	};

	const renderStatus = () => {
		if (!activeChat?.metadata?.statusMessage) return null;
		if (!activeChat?.metadata?.status) return null; // just in case...

		const { status, statusMessage } = activeChat.metadata;

		// Only show status for processing or stoppable states
		if (status !== ChatStatus.PROCESSING && status !== ChatStatus.STOPPABLE) {
			return null;
		}

		return (
			<Base.StatusMessage isMobile={isMobile}>
				<Base.StatusText>{statusMessage}</Base.StatusText>
				<Loader size={16} />
			</Base.StatusMessage>
		);
	};

	if (!activeChat) {
		return null;
	}

	return (
		<>
			<Base.Base onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
				<ChatDebugOverlay />
				<Base.ScrollingAreaContainer $fill={activeChat.entries.length > 0}>
					<Base.ScrollingArea
						ref={containerRef}
						data-chat-scroll-container
						$fill={activeChat.entries.length > 0}
					>
						{parentChat && renderEntries(parentChat, { isArchived: true })}
						{renderEntries(activeChat)}
						{renderStatus()}
						<div ref={spacerRef} />
					</Base.ScrollingArea>
					{isRestoring && (
						<Base.RestoreOverlay>
							<Loader size={24} />
						</Base.RestoreOverlay>
					)}
				</Base.ScrollingAreaContainer>
				{isDraggingFile && (
					<Base.DragOverlay>
						<Base.DragOverlayContent>
							<Icon type={IconType.ATTACHMENT} width={32} height={32} color={theme.gray[1000]} />
							<Base.DragOverlayText isMobile={isMobile}>Drop files to attach</Base.DragOverlayText>
						</Base.DragOverlayContent>
					</Base.DragOverlay>
				)}
					<Base.InputArea>
					{!isScrolledToBottom && !isRestoring && (
						<Base.JumpButton onClick={onJumpToBottom} isMobile={isMobile}>
							<Base.JumpButtonIcon>
								<Icon
									type={IconType.LINE_ARROW_DOWN}
									width={isMobile ? 24 : 18}
									height={isMobile ? 24 : 18}
									color={theme.gray[700]}
								/>
							</Base.JumpButtonIcon>
						</Base.JumpButton>
					)}
					{activeChat.metadata?.readOnly ? (
						<S.ReadOnlyNotice>This chat has been transitioned to read-only</S.ReadOnlyNotice>
					) : (
						<>
							<QueueIndicator chatType="scene" chatId={activeChat.id} />
							<SceneChatInput key={activeChat.id} chat={activeChat} />
						</>
					)}
				</Base.InputArea>
			</Base.Base>
		</>
	);
};

export default SceneConversation;
